/* CommonEditor.js */

"use strict";

/**
 * @typedef {{Control: {
 *  Textfield: {Textarea: TextareaControlComponent, InputText:InputTextControlComponent, InputNumber:InputNumberControlComponent},
 *  OptionSelection: {Single:{ComboBox:ComboBoxControlComponent}, Multiple:{ComboBox:MultipleComboBoxControlComponent}}
 *  Boolean: {InputCheckbox:InputCheckboxControlComponent}
 * }}} uiCoreTools
 */

/**
 * @type {uiCoreTools}
 */
const uiCore = require("@gluenetworks/steve_ui_core");
const InputTextControl = uiCore.Control.Textfield.InputText;

class CommonEditor {
    /**
     * Editor lifecycle:
     * construct -> loadValue(itemData) -> serializeValue -> isValueChanged -> validate ->
     *  -> serializeValue -> applyValue -> loadValue -> destroy
     *
     * @typedef {CustomFieldData} ItemData - Slick Grid data item
     * @typedef {{top: number,
     *           left: number,
     *           bottom: number,
     *           right: number,
     *           width: number,
     *           height: number,
     *           visible: boolean}} SlickPosition - abs position in SlickGrid
     *
     *
     * @param {Object}          options                 - initial editor options
     * @param {Object}          options.column          - Slick Grid column definition data
     * @param {Object}          options.container       - jQuery Slick Grid Cell object
     * @param {SlickGrid}       options.grid            - SlickGrid instance
     * @param {SlickPosition}   options.gridPosition    - Object describing cell position
     * @param {ItemData}        options.item            - Slick Grid data item
     * @param {SlickPosition}   options.position        - Abs SlickGrid grid cell position
     * @param {Function}        options.cancelChanges   - grid method to discard changes
     * @param {Function}        options.commitChanges   - grid method to store changes
     */
    constructor(options) {
        const columnData = options.column;
        const itemData = options.item;

        this.options = columnData.options || [];

        this.gridInstance = options.grid;
        this.cancelChanges = options.cancelChanges;
        this.commitChanges = options.commitChanges;

        this._itemData = itemData;
        this._isNewItem = Boolean(itemData.isNew);
        this._fieldName = columnData.field;
        this._$container = document.body; //options.container;
        this._$container.style.overflow = "hidden";

        this.initialData = itemData[this._fieldName];

        this.rootElement = this._createRootElement(this._fieldName);
        Object.assign(this.rootElement.style, {
            minWidth: options.position.width + "px",
            minHeight: options.position.height + "px",
        });
        this.position(options.position);

        this.input = this._createInput(this._itemData);

        this._init();
    }

    _init() {
        this.rootElement.append(this.input.renderer.rootElement);
        this._render();
        this._registerListeners();
    }

    _render() {
        this._$container.append(this.rootElement);
        this.focus();
    }

    _registerListeners() {
        this.rootElement.addEventListener(
            "keydown",
            (this._onRootKeydown = this._onRootKeydown.bind(this))
        );

        this.rootElement.addEventListener(
            "click",
            (this._onRootClick = this._onRootClick.bind(this))
        );
        this.input.addListener("UserValue", (this._onUserValue = this._onUserValue.bind(this)));
    }

    _unregisterListeners() {
        this.rootElement.removeEventListener("keydown", this._onRootKeydown);
        this.rootElement.removeEventListener("click", this._onRootClick);
        this.input.removeListener("UserValue", (this._onUserValue = this._onUserValue.bind(this)));
    }

    _onUserValue(/*e*/) {}

    /**
     *
     * @param {KeyboardEvent} e
     * @private
     */
    _onRootKeydown(e) {
        const isArrow = e.key.toLowerCase().match("arrow");
        const isEnter = e.key.toLowerCase() === "enter";
        const isEsc = e.key.toLowerCase() === "escape";
        const withCommand = e.metaKey || e.ctrlKey;

        const onInput = e.target.tagName === "INPUT";
        const onTextArea = e.target.tagName === "TEXTAREA";

        if ((onInput && isArrow) || (onTextArea && (isArrow || isEnter))) {
            // do not lose editor on arrow navigation in inputs
            e.stopPropagation();
        }

        if ((isEnter && withCommand) || (isEnter && onInput)) {
            this._save();
            e.stopPropagation();
        }

        if (isEsc) {
            this._cancel();
        }
    }
    _onRootClick(e) {
        e.preventDefault();
        e.stopPropagation();
    }

    _createRootElement(fieldName) {
        const rootElement = document.createElement("div");
        rootElement.classList.add("editor-wrapper", `${fieldName}-editor`);
        rootElement.component = this;
        return rootElement;
    }

    _createInput() {
        return new InputTextControl({
            darkTheme: true,
        });
    }

    _getValue() {
        return this.input.getValue();
    }

    _cancel() {
        this.input.setValue(this.initialData);
        this.cancelChanges();
    }

    _save() {
        this.commitChanges();
    }
    /*****************************/
    /*       PUBLIC API          */
    /*****************************/
    destroy() {
        this._unregisterListeners();
        this.rootElement.remove();
        this._$container.style.removeProperty("overflow");
        return Promise.resolve()
            .then(() => this.input.shutdown())
            .then(() => {
                this.input = null;
                this.rootElement = null;
                this.gridInstance = null;
                this._$container = null;
                this._itemData = null;
            });
    }

    focus() {
        this.input.focus();
    }

    show() {
        this.rootElement.style.removeProperty("display");
    }

    hide() {
        this.rootElement.style.setProperty("display", "none");
    }

    position({top, left}) {
        Object.assign(this.rootElement.style, {
            top: `${top}px`,
            left: `${left}px`,
        });
    }

    /**
     * is used to load value from item Data and set it to 'input'
     * @param {ItemData} item
     */
    loadValue(item) {
        const fieldValue = item[this._fieldName];
        this.initialData = fieldValue;
        this.input.setValue(fieldValue);
    }

    /**
     * is used to get data from 'input'
     * @returns {*}
     */
    serializeValue() {
        return this._getValue();
    }

    /**
     * is used to apply new data back to Item
     * @param {ItemData} item - grid data Item
     * @param {*} state - result of serializeValue
     */
    applyValue(item, state) {
        item[this._fieldName] = state;
    }

    /**
     * Compares initial and edited data.
     * If false the destroy will be called the next.
     * If true validate will be called the next.
     * @returns {boolean}
     */
    isValueChanged() {
        return JSON.stringify(this.initialData) !== JSON.stringify(this._getValue());
    }

    /**
     * checks if data is valid
     *
     * @returns {{valid: boolean, msg: ?string}}
     */

    validate() {
        const isInvalid = this.input.isInvalid();
        const msg = typeof isInvalid === "string" ? isInvalid : null;
        return {
            valid: !isInvalid,
            msg,
        };
    }
}

function withDialog(EditorClass, showDialog) {
    return class extends EditorClass {
        applyValue(item, state) {
            const activeCell = this.gridInstance.getActiveCell();
            const updateRow = this.gridInstance.updateRow;
            const updateItem = this.gridInstance.getData().updateItem;
            showDialog(item, state)
                .then(() => {
                    super.applyValue(item, state);
                    updateItem(item.id, item);
                    updateRow(activeCell.row);
                })
                .catch((error) => error && console.error(error));
        }
    };
}

module.exports = CommonEditor;

module.exports.withDialog = withDialog;
